export const CONTACT_VERIFICATION_REMINDER_MS = 24 * 60 * 60 * 1000;
export const MOCK_OTP_FAIL_CODE = "0000";
export const CONTACT_VERIFICATION_ROLE = {
  FAMILY: "family",
  CAREGIVER: "caregiver",
};

const ROLE_CONFIG = {
  [CONTACT_VERIFICATION_ROLE.FAMILY]: {
    emailKey: "familyEmailVerified",
    mobileKey: "familyMobileVerified",
    sessionKey: "familyVerificationPromptShown",
    lastShownKey: "familyVerificationPromptLastShownAt",
    profilePath: "/family-settings",
    apiRole: "FAMILYMEMBER",
  },
  [CONTACT_VERIFICATION_ROLE.CAREGIVER]: {
    emailKey: "caregiverEmailVerified",
    mobileKey: "caregiverMobileVerified",
    sessionKey: "caregiverVerificationPromptShown",
    lastShownKey: "caregiverVerificationPromptLastShownAt",
    profilePath: "/caregiver-profile",
    apiRole: "CAREGIVER",
  },
};

const normalizeVerificationRole = (role) => (role === CONTACT_VERIFICATION_ROLE.CAREGIVER ? CONTACT_VERIFICATION_ROLE.CAREGIVER : CONTACT_VERIFICATION_ROLE.FAMILY);

const getRoleConfig = (role) => ROLE_CONFIG[normalizeVerificationRole(role)];

export const getContactVerificationState = (role) => {
  const { emailKey, mobileKey } = getRoleConfig(role);
  return {
    emailVerified: localStorage.getItem(emailKey) === "true",
    mobileVerified: localStorage.getItem(mobileKey) === "true",
  };
};

export const syncContactVerificationFromProfile = (role, profile) => {
  const { emailKey, mobileKey } = getRoleConfig(role);
  localStorage.setItem(emailKey, profile.emailVerified ? "true" : "false");
  localStorage.setItem(mobileKey, profile.mobileVerified ? "true" : "false");
};

export const resetContactVerification = (role, target) => {
  const { emailKey, mobileKey } = getRoleConfig(role);
  const key = target === "email" ? emailKey : mobileKey;
  localStorage.setItem(key, "false");
};

export const markContactVerified = (role, target) => {
  const { emailKey, mobileKey } = getRoleConfig(role);
  const key = target === "email" ? emailKey : mobileKey;
  localStorage.setItem(key, "true");
};

export const needsContactVerification = (role) => {
  const { emailVerified, mobileVerified } = getContactVerificationState(role);
  return needsContactVerificationFromStatus(emailVerified, mobileVerified);
};

export const needsContactVerificationFromStatus = (emailVerified, mobileVerified) => !emailVerified || !mobileVerified;

export const getContactVerificationPromptMessageFromStatus = (emailVerified, mobileVerified) => {
  if (!emailVerified && !mobileVerified) {
    return "Please verify your email and mobile number to continue.";
  }
  if (!emailVerified) {
    return "Please verify your email address to continue.";
  }
  if (!mobileVerified) {
    return "Please verify your mobile number to continue.";
  }
  return "";
};

export const getContactVerificationPromptMessage = (role) => {
  const { emailVerified, mobileVerified } = getContactVerificationState(role);
  return getContactVerificationPromptMessageFromStatus(emailVerified, mobileVerified);
};

export const getContactVerificationProfilePath = (role) => getRoleConfig(role).profilePath;

export const clearContactVerificationPromptSession = (role) => {
  if (role) {
    const { sessionKey, lastShownKey } = getRoleConfig(role);
    sessionStorage.removeItem(sessionKey);
    localStorage.removeItem(lastShownKey);
    return;
  }

  Object.values(ROLE_CONFIG).forEach(({ sessionKey, lastShownKey }) => {
    sessionStorage.removeItem(sessionKey);
    localStorage.removeItem(lastShownKey);
  });
};

export const markContactVerificationPromptShown = (role) => {
  const { sessionKey, lastShownKey } = getRoleConfig(role);
  sessionStorage.setItem(sessionKey, "true");
  localStorage.setItem(lastShownKey, String(Date.now()));
};

/**
 * Show at most once per login session on home, and not again within
 * CONTACT_VERIFICATION_REMINDER_MS unless the user logs in again.
 */
export const shouldShowContactVerificationPrompt = (role) => {
  if (!needsContactVerification(role)) {
    return false;
  }

  const { sessionKey, lastShownKey } = getRoleConfig(role);
  if (sessionStorage.getItem(sessionKey) === "true") {
    return false;
  }

  const lastShown = Number(localStorage.getItem(lastShownKey) || 0);
  if (lastShown && Date.now() - lastShown < CONTACT_VERIFICATION_REMINDER_MS) {
    return false;
  }

  return true;
};

/** Mock OTP verify: any 4-digit code succeeds except 0000. */
export const submitContactOtp = (role, target, otp) => {
  const code = String(otp).replace(/\D/g, "").slice(0, 4);
  if (code.length !== 4) {
    return { success: false, error: "Please enter a 4-digit OTP." };
  }

  const { emailKey, mobileKey } = getRoleConfig(role);
  const storageKey = target === "email" ? emailKey : mobileKey;

  if (code === MOCK_OTP_FAIL_CODE) {
    localStorage.setItem(storageKey, "false");
    return { success: false, error: "Invalid OTP. Your contact is still not verified." };
  }

  localStorage.setItem(storageKey, "true");
  return { success: true };
};
